import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { LeonLoadingProvider } from "./components/LeonLoadingSystem";
import { AdProvider } from "./components/AdSystem";

// Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";

// Pages V1 (simplifiées)
import Home from "./pages/Home";
import AuthCallback from "./pages/AuthCallback";
import AuthPage from "./pages/AuthPage";
import DashboardV1 from "./pages/DashboardV1";
import AnalyzeQuote from "./pages/AnalyzeQuote";
import WorkRecipes from "./pages/WorkRecipes";
import PhotoDiagnostic from "./pages/PhotoDiagnostic";
import AIChat from "./pages/AIChat";
import HistoryPage from "./pages/HistoryPage";
import Subscription from "./pages/Subscription";
import AccountPage from "./pages/AccountPage";
import LegalPage from "./pages/LegalPage";
import TermsPage from "./pages/TermsPage";
import HowItWorks from "./pages/HowItWorks";

// Pages de fonctionnalités (landing pages dédiées)
import FeatureAnalyzeQuote from "./pages/features/FeatureAnalyzeQuote";
import FeatureRecipes from "./pages/features/FeatureRecipes";
import FeatureDiagnostic from "./pages/features/FeatureDiagnostic";
import FeatureChat from "./pages/features/FeatureChat";

// Pages imprimables
import PrintableReport from "./pages/PrintableReport";
import PrintableRecipe from "./pages/PrintableRecipe";

// Admin pages
import AdminLeads from "./pages/AdminLeads";

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  // Auth page - no navbar/footer
  if (location.pathname === '/auth' || location.pathname === '/login' || location.pathname === '/register') {
    return (
      <Routes>
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/login" element={<AuthPage />} />
        <Route path="/register" element={<AuthPage />} />
      </Routes>
    );
  }
  
  // Printable report - no navbar/footer
  if (location.pathname.startsWith('/report/')) {
    return (
      <Routes>
        <Route
          path="/report/:id"
          element={
            <ProtectedRoute>
              <PrintableReport />
            </ProtectedRoute>
          }
        />
      </Routes>
    );
  }
  
  // Printable recipe - no navbar/footer
  if (location.pathname.startsWith('/recipe-report/')) {
    return (
      <Routes>
        <Route
          path="/recipe-report/:recipeId"
          element={
            <ProtectedRoute>
              <PrintableRecipe />
            </ProtectedRoute>
          }
        />
      </Routes>
    );
  }
  
  return (
    <>
      <Navbar />
      <Routes>
        {/* Pages publiques */}
        <Route path="/" element={<Home />} />
        <Route path="/comment-ca-marche" element={<HowItWorks />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/conditions-generales" element={<TermsPage />} />
        <Route path="/subscription" element={<Subscription />} />
        <Route path="/subscription/success" element={<Subscription />} />
        
        {/* Pages de fonctionnalités (landing pages dédiées) */}
        <Route path="/fonctionnalite/analyse-devis" element={<FeatureAnalyzeQuote />} />
        <Route path="/fonctionnalite/recettes-travaux" element={<FeatureRecipes />} />
        <Route path="/fonctionnalite/diagnostic-photo" element={<FeatureDiagnostic />} />
        <Route path="/fonctionnalite/assistant" element={<FeatureChat />} />
        
        {/* Dashboard V1 - Page principale après connexion */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardV1 />
            </ProtectedRoute>
          }
        />
        
        {/* Pilier 1: Analyse de devis */}
        <Route
          path="/analyze"
          element={
            <ProtectedRoute>
              <AnalyzeQuote />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analyze/:id"
          element={
            <ProtectedRoute>
              <AnalyzeQuote />
            </ProtectedRoute>
          }
        />
        
        {/* Pilier 2: Recettes travaux */}
        <Route
          path="/recipes"
          element={
            <ProtectedRoute>
              <WorkRecipes />
            </ProtectedRoute>
          }
        />
        <Route
          path="/recipes/:recipeId"
          element={
            <ProtectedRoute>
              <WorkRecipes />
            </ProtectedRoute>
          }
        />
        
        {/* Pilier 3: Diagnostic photo */}
        <Route
          path="/diagnostic"
          element={
            <ProtectedRoute>
              <PhotoDiagnostic />
            </ProtectedRoute>
          }
        />
        <Route
          path="/diagnostic/:id"
          element={
            <ProtectedRoute>
              <PhotoDiagnostic />
            </ProtectedRoute>
          }
        />
        
        {/* Pilier 4: Assistant chat */}
        <Route
          path="/chat"
          element={
            <ProtectedRoute>
              <AIChat />
            </ProtectedRoute>
          }
        />
        
        {/* Historique */}
        <Route
          path="/historique"
          element={
            <ProtectedRoute>
              <HistoryPage />
            </ProtectedRoute>
          }
        />
        
        {/* Compte */}
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <AccountPage />
            </ProtectedRoute>
          }
        />
        
        {/* Admin - Dashboard Leads */}
        <Route
          path="/admin/leads"
          element={
            <ProtectedRoute>
              <AdminLeads />
            </ProtectedRoute>
          }
        />
        
        {/* Page 404 */}
        <Route
          path="*"
          element={
            <div className="min-h-screen bg-black-rich flex items-center justify-center px-4">
              <div className="text-center">
                <h1 className="text-6xl font-bold text-gold-500 mb-4">404</h1>
                <p className="text-xl text-zinc-400 mb-6">Page introuvable</p>
                <a href="/dashboard" className="px-6 py-3 bg-gold-500 text-black font-bold rounded-lg hover:bg-gold-400 transition-colors">
                  Retour au tableau de bord
                </a>
              </div>
            </div>
          }
        />
      </Routes>
      <Footer />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App dark">
        <BrowserRouter>
          <AuthProvider>
            <AdProvider>
              <LeonLoadingProvider>
                <AppRouter />
                <Toaster position="top-right" />
              </LeonLoadingProvider>
            </AdProvider>
          </AuthProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
